import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { formatPriceWithEur } from '../../utils/priceFormatter';
import './style.less';

/**
 * PromotionalPrice Component
 * Displays pricing with optional discount visualization
 *
 * @param {string} originalPrice - Original price (before discount)
 * @param {number} discountedPrice - Discounted price
 * @param {number} discountPercent - Discount percentage
 * @param {boolean} showDiscount - Whether to show discount
 * @param {string} size - Size variant: 'small' | 'medium' | 'large'
 * @param {object} discountNote - Optional note for special discounts (e.g., free consultation)
 */
export const PromotionalPrice = ({
  originalPrice,
  discountedPrice,
  discountPercent = 0,
  showDiscount = false,
  size = 'medium',
  discountNote = null,
}) => {
  const { t } = useTranslation();

  // If no discount, show regular price with EUR
  if (!showDiscount || discountPercent === 0) {
    const formattedPrice = formatPriceWithEur(originalPrice);
    return <div className={`promotional-price size-${size}`}>{formattedPrice}</div>;
  }

  // Check if it's a 100% discount (free)
  const isFree = discountPercent === 100;

  // Format discounted price with EUR
  const formatPrice = (price) => {
    if (typeof price === 'number') {
      return formatPriceWithEur(price);
    }
    return formatPriceWithEur(price);
  };

  return (
    <motion.div
      className={`promotional-price with-discount size-${size}`}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="price-wrapper">
        <div
          className={`discounted-price ${isFree ? 'free' : ''}`}
          aria-label={`${t('campaign.discount')} ${discountPercent}%`}
        >
          {isFree ? (
            <span className="free-label">
              {discountNote || t('campaign.free')}
            </span>
          ) : (
            formatPrice(discountedPrice)
          )}
        </div>
        <div className="original-price" aria-label={t('campaign.originalPrice')}>
          {formatPriceWithEur(originalPrice)}
        </div>
      </div>
    </motion.div>
  );
};
